import React, { useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface InteractiveDiagramComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const InteractiveDiagramComponent: React.FC<InteractiveDiagramComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [activeStage, setActiveStage] = useState<string | null>(null);
  
  const stages = resource.stages || [];
  const isClickable = resource.clickable || resource.interactive || false;
  const stageData = resource.stage_data || {};

  const handleStageClick = (stage: string) => {
    if (isClickable) {
      setActiveStage(stage);
      onAction?.('stage_clicked', { stage });
    }
  };

  return (
    <div className="bg-blue-50 rounded-lg p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-blue-900 mb-2">{t('interactiveDiagram.interactiveDiagram')}</h3>
        <p className="text-sm text-blue-700">{t('interactiveDiagram.clickOnStagesToLearnMore')}</p>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
        <div className="grid grid-cols-2 gap-3">
          {stages.map((stage: string, index: number) => (
            <button
              key={stage}
              onClick={() => handleStageClick(stage)}
              disabled={!isClickable}
              className={`p-3 rounded-lg border-2 transition-all min-h-[80px] ${
                activeStage === stage
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              } ${!isClickable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="text-center">
                <div className="w-8 h-8 bg-blue-500 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold">
                  {stageData[stage]?.icon || (index + 1)}
                </div>
                <span className="text-xs font-medium leading-tight break-words">
                  {stageData[stage]?.title || stage.charAt(0).toUpperCase() + stage.slice(1)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {activeStage && stageData[activeStage] && (
        <div className="mt-3 p-4 bg-blue-100 rounded-lg">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-2">{stageData[activeStage].icon}</span>
            <h4 className="font-semibold text-blue-900 text-lg">
              {stageData[activeStage].title}
            </h4>
          </div>
          <p className="text-sm text-blue-800 mb-2">
            {stageData[activeStage].description}
          </p>
          <p className="text-xs text-blue-700">
            {stageData[activeStage].details}
          </p>
        </div>
      )}
    </div>
  );
};
